<script>
import routeType from '@/router/routeType'
import paramType from '@/router/paramType'
import RestApiType from '@/api/RestApiType'
import MapperType from '@/services/mapper/MapperType'
import RestaurantDeliveryAreaType from '@/enum/RestaurantDeliveryAreaType'
import RestaurantPriceCategoryType from '@/enum/RestaurantPriceCategoryType'
import { ROLE_ADMIN, ROLE_RESTAURANT } from '@/enum/roles'

import FormTabs from '@/components/form/FormTabs'
import FormTab from '@/components/form/FormTab'
import RestaurantSelector from '@/components/layout/components/RestaurantSelector'
import GeneralTab from './tabs/General'
import ContractualTab from './tabs/Contractual'
import OpenHoursTab from './tabs/OpenHours'
import TokenTab from './tabs/Token'
import DeliverySettingsTab from './tabs/DeliverySettings'
import OrderTransferTab from './tabs/OrderTransfer'
import MenuTab from './tabs/Menu'
import MealOptionGroupsTab from './tabs/MealOptionGroups'
import MealImages from './tabs/MealImages'

import AuthMixin from '@/components/mixins/AuthMixin'
import FormBase from '@/components/mixins/FormBase'
import { mapGetters } from 'vuex'
import authType from '@/store/type/authType'
import { EventBus } from '@/plugins/events'

export default {
    components: {
        RestaurantSelector,
        FormTabs,
        FormTab,
        GeneralTab,
        ContractualTab,
        OpenHoursTab,
        TokenTab,
        DeliverySettingsTab,
        OrderTransferTab,
        MenuTab,
        MealOptionGroupsTab,
        MealImages,
    },

    mixins: [FormBase, AuthMixin],

    data() {
        return {
            api: RestApiType.RESTAURANTS,
            mapper: MapperType.RESTAURANT_FORM,
            form: {
                name: '',
                phone: '',
                tags: [],
                translations: {},
                isActive: true,
                isVisibleForCompany: true,
                isVisibleForPublic: true,
                areas: [],
                billingInfo: {
                    regCompanyName: null,
                    registrationNumber: null,
                    regAddressCountry: null,
                    regAddressPostcode: null,
                    regAddressStreetNumber: null,
                    regAddressStreet: null,
                    accountNumber: null,
                    sortCode: null,
                    vatNumber: null,
                    commOnFood: null,
                    commOnDelivery: null,
                    cardTransactionFee: null,
                    billingContactName: null,
                    billingContactEmail: null,
                    billingContactPhone: null,
                    paymentTerm: null,
                    selfBilling: null,
                    orderContactPhone: null,
                    orderContactEmail: null,
                    orderContactName: null,
                },
                openingDays: [],
                specialClosures: [],
                location: '',
                longitude: null,
                latitude: null,
                isPreorder: false,
                isPreorderDiffCalendar: false,
                preorderBorderTime: '00:00',
                preorderDayBefore: 1,
                preorderDayUntil: 60,
                deliveryAreaType: RestaurantDeliveryAreaType.custom,
                orditSoftwareSetting: null,
                priceCategory: RestaurantPriceCategoryType.average,
                isCatering: null,
                exportEnabled: true,
                hiddenCompanies: [],
                isOpeningHoursEditEnabled: true,
                coverImageMedia: null,
                cardImageMedia: null,
                approveEmail: null,
                additionalApproveEmails: [],
                accessToken: null,
                timezoneRegion: null,
                pickupInformation: {},
                deliverySetting: {
                    defaultPreparationTime: null,
                    deliveryRules: [],
                },
                isEnabledForRestaurantAdmin: true,
                sendOnlyCanteenConfirmEmail: false,
                deliverooUrl: null,
            },
            editRoute: routeType.RESTAURANT_EDIT,
            listRoute: routeType.RESTAURANT_LIST,
        }
    },
    computed: {
        ...mapGetters({
            isRestaurantAdmin: authType.getters.IS_RESTAURANT_ADMIN,
        }),
        tabs() {
            return [
                {
                    name: 'general',
                    title: this.$t('labels.general_settings'),
                    roles: [ROLE_ADMIN],
                    component: GeneralTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'contractual',
                    title: this.$t('labels.contractual'),
                    roles: [ROLE_ADMIN],
                    component: ContractualTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'businesshours',
                    title: this.$t('labels.business_hours'),
                    roles: [
                        ROLE_ADMIN,
                        this.businessHoursPage ? ROLE_RESTAURANT : null,
                    ],
                    component: OpenHoursTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'deliverySettings',
                    title: this.$t('labels.delivery_settings'),
                    roles: [ROLE_ADMIN],
                    component: DeliverySettingsTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'orderTransfer',
                    title: this.$t('labels.order_transfer'),
                    disabled: !this.form['@id'],
                    roles: [ROLE_ADMIN],
                    component: OrderTransferTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'menu',
                    title: this.$t('labels.menu'),
                    disabled: !this.form['@id'],
                    roles: [ROLE_ADMIN, this.menuPage ? ROLE_RESTAURANT : null],
                    component: MenuTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'mealOptionGroups',
                    title: this.$t('labels.meal_option_groups'),
                    disabled: !this.form['@id'],
                    roles: [ROLE_ADMIN, this.menuPage ? ROLE_RESTAURANT : null],
                    component: MealOptionGroupsTab,
                    tabError: this.tabErrorsContainer,
                },
                {
                    name: 'mealImages',
                    title: this.$t('labels.meal_images'),
                    disabled: !this.form['@id'],
                    roles: [ROLE_ADMIN],
                    component: MealImages,
                    tabError: {},
                },
                {
                    name: 'token',
                    title: this.$t('labels.token'),
                    disabled: !this.form['@id'],
                    roles: [ROLE_ADMIN],
                    component: TokenTab,
                    tabError: {},
                },
            ].filter((tabItem) => {
                let enabled = true

                if (tabItem.roles) {
                    const roleIntersection = tabItem.roles.filter((item) =>
                        this.userRoles.includes(item)
                    )

                    enabled = roleIntersection.length > 0
                }
                return enabled
            })
        },

        businessHoursPage() {
            return (
                this.isRestaurantAdmin &&
                this.$route.params.page === paramType.RESTAURANT_BUSINESS_HOURS
            )
        },

        menuPage() {
            return (
                this.isRestaurantAdmin &&
                this.$route.params.page === paramType.RESTAURANT_MENU
            )
        },

        disableSaveButton() {
            return (
                this.activeTab > 4 ||
                (this.isRestaurantAdmin && !this.businessHoursPage)
            )
        },

        tabErrors() {
            const tabErrors = {}
            Object.keys(this.tabErrorsContainer).forEach((tabName) => {
                Object.keys(this.tabErrorsContainer[tabName]).forEach(
                    (propertyName) => {
                        if (!tabErrors[tabName]) {
                            tabErrors[tabName] = 0
                        }
                        if (
                            this.tabErrorsContainer[tabName][propertyName] > 0
                        ) {
                            tabErrors[tabName]++
                        }
                    }
                )
            })
            return tabErrors
        },

        activeTab: {
            get() {
                return parseInt(this.$route.query.tab) || 0
            },
            set(val) {
                this.$router.replace({ query: { tab: val } })
            },
        },
    },

    mounted() {
        if (this.isRestaurantAdmin)
            EventBus.$on('reloadRestaurant', this.$refs.form.load)
    },

    beforeDestroy() {
        if (this.isRestaurantAdmin)
            EventBus.$off('reloadRestaurant', this.$refs.form.load)
    },
}
</script>

<template>
    <base-form
        ref="form"
        v-model="form"
        :api="api"
        :mapper="mapper"
        :entity-id="entityId"
        :title="title"
        :disabled="disabled"
        :disable-save-button="disableSaveButton"
        :disable-delete-button="true"
        @created="created"
        @updated="updated"
        @cancelled="cancelled"
        @deleted="deleted"
    >
        <!-- RESTAURANT SELECTOR -->
        <restaurant-selector
            v-if="isRestaurantAdmin"
            class="mb-4"
        ></restaurant-selector>
        <form-tabs
            v-model="activeTab"
            :hide-header="businessHoursPage"
            :class="['pb-16']"
        >
            <form-tab
                v-for="tab in tabs"
                v-show="!businessHoursPage"
                :key="tab.name"
                :tab-errors="tabErrors"
                :name="tab.name"
                :title="tab.title"
                :disabled="!!tab.disabled"
            ></form-tab>

            <v-tabs-items
                :key="`tabsContent-${tabs ? tabs.length : '0'}`"
                v-model="activeTab"
            >
                <component
                    :is="tab.component"
                    v-for="tab in tabs"
                    :key="`tabItem-${tab.name}`"
                    v-model="form"
                    @tabError="
                        tabErrorsContainer = {
                            ...tab.tabError,
                            ...$event,
                        }
                    "
                >
                </component>
            </v-tabs-items>
        </form-tabs>
    </base-form>
</template>
